import { supabase } from '../../config/supabase';
import { Alert, AlertStatus } from '../../types/alert';
import { AlertHistory, AlertHistoryCreateInput, AlertActionType, RawAlertHistory } from '../../types/alertHistory';
import logger from '../../utils/logger';

/**
 * Convertit un objet RawAlertHistory en AlertHistory
 * @param raw Données brutes de l'historique d'alerte
 * @returns Objet AlertHistory formaté
 */
const convertRawToAlertHistory = (raw: RawAlertHistory): AlertHistory => {
  return {
    id: raw.id,
    alertId: raw.alert_id,
    actionType: raw.action_type as AlertActionType,
    previousStatus: raw.previous_status as AlertStatus | undefined,
    newStatus: raw.new_status as AlertStatus | undefined,
    previousData: raw.previous_data,
    newData: raw.new_data,
    createdAt: new Date(raw.created_at),
    createdBy: raw.created_by
  };
};

export const alertHistoryService = {
  /**
   * Récupère tout l'historique des alertes
   * @returns Liste des historiques d'alertes
   */
  async getAll(): Promise<AlertHistory[]> {
    try {
      const { data, error } = await supabase
        .from('alert_history')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(item => convertRawToAlertHistory(item as RawAlertHistory));
    } catch (error) {
      logger.error('Erreur lors de la récupération de l\'historique des alertes:', error);
      throw error;
    }
  },

  /**
   * Récupère l'historique d'une alerte spécifique
   * @param alertId ID de l'alerte
   * @returns Liste des entrées d'historique pour cette alerte
   */
  async getByAlertId(alertId: string): Promise<AlertHistory[]> {
    try {
      const { data, error } = await supabase
        .from('alert_history')
        .select('*')
        .eq('alert_id', alertId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map(item => convertRawToAlertHistory(item as RawAlertHistory));
    } catch (error) {
      logger.error(`Erreur lors de la récupération de l'historique de l'alerte ${alertId}:`, error);
      throw error;
    }
  },

  /**
   * Crée une nouvelle entrée dans l'historique des alertes
   * @param input Données pour la création de l'historique
   * @returns L'entrée d'historique créée
   */
  async create(input: AlertHistoryCreateInput): Promise<AlertHistory> {
    try {
      const { alertId, actionType, previousStatus, newStatus, previousData, newData, createdBy } = input;

      const { data, error } = await supabase
        .from('alert_history')
        .insert({
          alert_id: alertId,
          action_type: actionType,
          previous_status: previousStatus,
          new_status: newStatus,
          previous_data: previousData,
          new_data: newData,
          created_by: createdBy || null
        })
        .select()
        .single();

      if (error) throw error;

      return convertRawToAlertHistory(data as RawAlertHistory);
    } catch (error) {
      logger.error('Erreur lors de la création d\'une entrée d\'historique d\'alerte:', error);
      throw error;
    }
  },

  /**
   * Enregistre un changement de statut dans l'historique
   * @param alertId ID de l'alerte
   * @param previousStatus Statut précédent
   * @param newStatus Nouveau statut
   * @param createdBy ID de l'utilisateur qui a effectué le changement (optionnel)
   * @returns L'entrée d'historique créée
   */
  async logStatusChange(alertId: string, previousStatus: AlertStatus, newStatus: AlertStatus, createdBy?: string): Promise<AlertHistory> {
    return this.create({
      alertId,
      actionType: 'status_changed',
      previousStatus,
      newStatus,
      createdBy
    });
  },

  /**
   * Enregistre une mise à jour d'alerte dans l'historique
   * @param alertId ID de l'alerte
   * @param previousData Données précédentes
   * @param newData Nouvelles données
   * @param createdBy ID de l'utilisateur qui a effectué la mise à jour (optionnel)
   * @returns L'entrée d'historique créée
   */
  async logUpdate(alertId: string, previousData: Partial<Alert>, newData: Partial<Alert>, createdBy?: string): Promise<AlertHistory> {
    return this.create({
      alertId,
      actionType: 'updated',
      previousData,
      newData,
      createdBy
    });
  },

  /**
   * Supprime une entrée d'historique
   * @param id ID de l'entrée d'historique à supprimer
   */
  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('alert_history')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      logger.error(`Erreur lors de la suppression de l'entrée d'historique ${id}:`, error);
      throw error;
    }
  }
};

